/**
 * Attack-path record schema — DataTable columns + FormModal fields
 * for Step 5 (`attack_path_feasibility_agent`).
 *
 * Real DB shape: per ISO/SAE 21434 Annex G, each path has five
 * factor dimensions (each enum + numeric value), a derived total
 * attack potential, and a four-tier feasibility rating.
 *
 *   { attack_path_id, threat_id, asset_id,
 *     attack_path, attack_vector, threat_agent, preconditions[],
 *     elapsed_time + elapsed_time_value,
 *     specialist_expertise + expertise_value,
 *     knowledge_of_item + knowledge_value,
 *     window_of_opportunity + window_value,
 *     equipment + equipment_value,
 *     total_attack_potential,
 *     attack_feasibility_rating, justification,
 *     // hierarchical (when source === 'child_tara'):
 *     source, source_session_id, is_relevant, relevance_justification, ... }
 *
 * Feasibility palette comes from `severityStyle.getFeasibilityColor`
 * — keeps Step 5 consistent with the impact palette in Step 3.
 */
import React from 'react';
import LevelBadge from '../../../../components/ui/LevelBadge';
import CategoryPill from '../../../../components/ui/CategoryPill';
import { getFeasibilityColor } from '../../../../utils/severityStyle';

export interface AttackPathRecord {
  attack_path_id: string;
  threat_id?: string;
  asset_id?: string;
  attack_path?: string;
  attack_vector?: string;
  threat_agent?: string;
  preconditions?: string[];
  elapsed_time?: string;
  specialist_expertise?: string;
  knowledge_of_item?: string;
  window_of_opportunity?: string;
  equipment?: string;
  total_attack_potential?: number;
  attack_feasibility_rating?: string;
  justification?: string;
  /** Hierarchical mode (`source === 'child_tara'` flags an inherited row). */
  source?: string;
  source_session_id?: string;
  is_relevant?: boolean;
  relevance_justification?: string;
  [key: string]: unknown;
}

/** Annex G enum option lists — exposed so the form modal can render
 *  them as <select> options without re-declaring per consumer. */
export const ATTACK_VECTORS = [
  { value: 'Physical', label: 'Physical' },
  { value: 'Local', label: 'Local' },
  { value: 'Adjacent', label: 'Adjacent' },
  { value: 'Network', label: 'Network' },
];

export const ELAPSED_TIME_OPTIONS = [
  { value: '<1 day', label: '<1 day' },
  { value: '<1 week', label: '<1 week' },
  { value: '<1 month', label: '<1 month' },
  { value: '<6 months', label: '<6 months' },
  { value: '>6 months', label: '>6 months' },
];

export const EXPERTISE_OPTIONS = [
  { value: 'Layman', label: 'Layman' },
  { value: 'Proficient', label: 'Proficient' },
  { value: 'Expert', label: 'Expert' },
  { value: 'Multiple experts', label: 'Multiple experts' },
];

export const KNOWLEDGE_OPTIONS = [
  { value: 'Public', label: 'Public' },
  { value: 'Restricted', label: 'Restricted' },
  { value: 'Confidential', label: 'Confidential' },
  { value: 'Strictly confidential', label: 'Strictly confidential' },
];

export const WINDOW_OPTIONS = [
  { value: 'Unlimited', label: 'Unlimited' },
  { value: 'Easy', label: 'Easy' },
  { value: 'Moderate', label: 'Moderate' },
  { value: 'Difficult/none', label: 'Difficult/none' },
];

export const EQUIPMENT_OPTIONS = [
  { value: 'Standard', label: 'Standard' },
  { value: 'Specialized', label: 'Specialized' },
  { value: 'Bespoke', label: 'Bespoke' },
  { value: 'Multiple bespoke', label: 'Multiple bespoke' },
];

export const FEASIBILITY_OPTIONS = [
  { value: 'Very Low', label: 'Very Low' },
  { value: 'Low', label: 'Low' },
  { value: 'Medium', label: 'Medium' },
  { value: 'High', label: 'High' },
];

const joinList = (v: unknown): string =>
  Array.isArray(v) ? v.join('; ') : v == null ? '' : String(v);

const renderFeasibility = (row: AttackPathRecord) =>
  row.attack_feasibility_rating ? (
    <LevelBadge
      color={getFeasibilityColor(row.attack_feasibility_rating)}
      label={String(row.attack_feasibility_rating)}
      size="sm"
    />
  ) : (
    ''
  );

const renderVector = (row: AttackPathRecord) =>
  row.attack_vector ? <CategoryPill label={String(row.attack_vector)} size="sm" /> : '';

/** Every DB field surfaced in the table — column widths use pixel
 *  values so the table extends past the container width and the
 *  `.tableScroll` parent picks up horizontal scrolling. Each Annex G
 *  qualitative factor sits next to its numeric value for at-a-glance
 *  comparison; `total_attack_potential` aggregates all five.
 *
 *  Edit mode appends the `_actions` kebab as a 56px trailing column;
 *  read mode skips it. */
export const ATTACK_PATH_COLUMNS_READ = [
  { key: 'attack_path_id', label: 'ID', sortable: true, width: 100 },
  { key: 'threat_id', label: 'Threat', sortable: true, width: 110 },
  { key: 'asset_id', label: 'Asset', sortable: true, width: 110 },
  { key: 'attack_vector', label: 'Vector', sortable: true, width: 110, render: renderVector },
  { key: 'threat_agent', label: 'Agent', sortable: true, width: 130 },
  { key: 'attack_path', label: 'Attack Path', sortable: false, width: 280 },
  {
    key: 'preconditions',
    label: 'Preconditions',
    sortable: false,
    width: 200,
    render: (row: AttackPathRecord) => joinList(row.preconditions),
  },
  { key: 'elapsed_time', label: 'Time', sortable: true, width: 110 },
  { key: 'elapsed_time_value', label: 'Time#', sortable: true, width: 70, align: 'right' },
  { key: 'specialist_expertise', label: 'Expertise', sortable: true, width: 120 },
  { key: 'expertise_value', label: 'Exp#', sortable: true, width: 70, align: 'right' },
  { key: 'knowledge_of_item', label: 'Knowledge', sortable: true, width: 130 },
  { key: 'knowledge_value', label: 'Know#', sortable: true, width: 70, align: 'right' },
  { key: 'window_of_opportunity', label: 'Window', sortable: true, width: 140 },
  { key: 'window_value', label: 'Win#', sortable: true, width: 70, align: 'right' },
  { key: 'equipment', label: 'Equipment', sortable: true, width: 130 },
  { key: 'equipment_value', label: 'Equip#', sortable: true, width: 70, align: 'right' },
  { key: 'total_attack_potential', label: 'Total', sortable: true, width: 80, align: 'right' },
  { key: 'attack_feasibility_rating', label: 'Feasibility', sortable: true, width: 110, render: renderFeasibility },
  { key: 'justification', label: 'Justification', sortable: false, width: 260 },
];

export const ATTACK_PATH_COLUMNS_EDIT = ATTACK_PATH_COLUMNS_READ;

export const ATTACK_PATH_ACTIONS_COLUMN_WIDTH = 56;

const ATTACK_PATH_ID_PATTERN = /^[A-Z0-9_-]+$/;

/** FormModal field config. `threat_id` + `asset_id` options come from
 *  the consumer (Step 4 + Step 2 fetches) at render time. The 5 Annex G
 *  factor values are agent-derived numerics — we don't expose them on
 *  the form (the form sets the qualitative enum; the backend recomputes
 *  values + total_attack_potential when saving). */
export const ATTACK_PATH_FORM_FIELDS = [
  {
    name: 'attack_path_id',
    label: 'Attack Path ID',
    type: 'text',
    required: true,
    autoFocus: true,
    placeholder: 'e.g. AP-001',
    helpText: 'Unique identifier — uppercase letters, numbers, underscores, hyphens.',
    validate: (v: unknown) => {
      const s = String(v ?? '').trim();
      if (!s) return 'Attack Path ID is required';
      if (!ATTACK_PATH_ID_PATTERN.test(s)) {
        return 'Attack Path ID must contain only uppercase letters, numbers, underscores, and hyphens';
      }
      return null;
    },
    width: 'half',
  },
  {
    name: 'attack_feasibility_rating',
    label: 'Feasibility Rating',
    type: 'select',
    required: true,
    options: FEASIBILITY_OPTIONS,
    helpText: 'Overall attack feasibility per ISO/SAE 21434 Annex G.',
    width: 'half',
  },
  {
    name: 'threat_id',
    label: 'Related Threat',
    type: 'select',
    required: true,
    // Options injected by AttackPathFormModal from Step 4 threats.
    options: [] as Array<{ value: string; label: string }>,
    helpText: 'The threat this attack path exploits.',
    width: 'half',
  },
  {
    name: 'asset_id',
    label: 'Target Asset',
    type: 'select',
    required: true,
    // Options injected from Step 2 assets.
    options: [] as Array<{ value: string; label: string }>,
    width: 'half',
  },
  {
    name: 'attack_vector',
    label: 'Attack Vector',
    type: 'select',
    required: true,
    options: ATTACK_VECTORS,
    width: 'half',
  },
  {
    name: 'threat_agent',
    label: 'Threat Agent',
    type: 'text',
    required: true,
    placeholder: 'e.g. Remote Attacker, Malicious Insider',
    width: 'half',
  },
  {
    name: 'attack_path',
    label: 'Attack Path Description',
    type: 'textarea',
    required: true,
    rows: 4,
    placeholder: 'Describe how the attacker exploits the threat — actor, vector, steps.',
    validate: (v: unknown) => {
      const s = String(v ?? '').trim();
      if (s.length < 20) return 'Attack path description must be at least 20 characters.';
      return null;
    },
  },
  {
    name: 'preconditions',
    label: 'Preconditions',
    type: 'textarea',
    required: false,
    rows: 2,
    placeholder: 'Conditions that must hold for the attack to succeed (separate with semicolons).',
    helpText: 'Saved as a list — semicolons split entries.',
    /** preconditions[] on disk; the form keeps them as a single
     *  semicolon-separated text field for simpler editing. The
     *  consumer's `handleSave` is responsible for splitting. */
  },
  {
    name: 'elapsed_time',
    label: 'Elapsed Time',
    type: 'select',
    required: true,
    options: ELAPSED_TIME_OPTIONS,
    width: 'half',
  },
  {
    name: 'specialist_expertise',
    label: 'Expertise',
    type: 'select',
    required: true,
    options: EXPERTISE_OPTIONS,
    width: 'half',
  },
  {
    name: 'knowledge_of_item',
    label: 'Knowledge of Item',
    type: 'select',
    required: true,
    options: KNOWLEDGE_OPTIONS,
    width: 'half',
  },
  {
    name: 'window_of_opportunity',
    label: 'Window of Opportunity',
    type: 'select',
    required: true,
    options: WINDOW_OPTIONS,
    width: 'half',
  },
  {
    name: 'equipment',
    label: 'Equipment Required',
    type: 'select',
    required: true,
    options: EQUIPMENT_OPTIONS,
    width: 'half',
  },
  {
    name: 'justification',
    label: 'Justification',
    type: 'textarea',
    required: false,
    rows: 3,
    placeholder: 'Rationale for the feasibility rating.',
  },
];

/** Helper for the consumer's handleSave: turn the form's semicolon-
 *  separated `preconditions` textarea value back into an array, and
 *  drop empty entries. Returns the patched record. */
export function normaliseAttackPathRecord(
  values: Record<string, unknown>,
): Record<string, unknown> {
  const pre = values.preconditions;
  if (typeof pre === 'string') {
    return {
      ...values,
      preconditions: pre
        .split(/[;\n]/)
        .map((s) => s.trim())
        .filter(Boolean),
    };
  }
  return values;
}
